import React, { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  User,
  Mail,
  Phone,
  MapPin,
  Shield,
  CheckCircle,
  XCircle,
  Clock,
  Activity,
  Settings,
  Copy,
  Check,
  Hash,
} from "lucide-react";

const EmployeeCard = ({ employee, index }) => {
  const navigate = useNavigate();
  const [copiedId, setCopiedId] = useState(false);

  const handleCardClick = () => {
    navigate(`/employees/${employee.id}`);
  };

  const handlePermissionsClick = (e) => {
    e.stopPropagation();
    navigate(`/employees/${employee.id}/permissions`);
  };

  const handleCopyId = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(employee.id.toString()).then(() => {
      setCopiedId(true);
      toast.success("تم نسخ معرف المستخدم");
      setTimeout(() => setCopiedId(false), 2000);
    }).catch(() => {
      toast.error("فشل في نسخ معرف المستخدم");
    });
  };

  const getRoleColor = (role) => {
    switch (role) {
      case "super":
        return "bg-purple-900/30 text-purple-300 border-purple-500/30";
      case "admin":
        return "bg-blue-900/30 text-blue-300 border-blue-500/30";
      default:
        return "bg-slate-800/30 text-slate-300 border-slate-600/30";
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("ar-EG", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
      onClick={handleCardClick}
      className="bg-slate-800/50 backdrop-blur-sm rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer p-6 border border-slate-700/50 hover:border-blue-500/50 group"
      dir="rtl"
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-purple-600 rounded-full flex items-center justify-center shadow-lg">
            <User className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-white text-lg group-hover:text-blue-300 transition-colors">
              {employee.full_name}
            </h3>
            <p className="text-slate-400 text-sm">@{employee.username}</p>
            {/* User ID with Copy Button */}
            <div className="flex items-center gap-2 mt-1">
              <div className="flex items-center gap-1 text-xs text-slate-500">
                <Hash className="w-3 h-3" />
                <span>{employee.id}</span>
              </div>
              <button
                onClick={handleCopyId}
                className="p-1 hover:bg-slate-700/50 rounded transition-colors group/copy"
                title="نسخ معرف المستخدم"
              >
                {copiedId ? (
                  <Check className="w-3 h-3 text-green-400" />
                ) : (
                  <Copy className="w-3 h-3 text-slate-500 group-hover/copy:text-slate-300" />
                )}
              </button>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {employee.is_active ? (
            <CheckCircle className="w-5 h-5 text-green-400" />
          ) : (
            <XCircle className="w-5 h-5 text-red-400" />
          )}
        </div>
      </div>

      {/* Role Badge */}
      <div className="mb-4">
        <span
          className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium border ${getRoleColor(
            employee.role
          )}`}
        >
          <Shield className="w-4 h-4" />
          {employee.role_display}
        </span>
      </div>

      {/* Contact Info */}
      <div className="space-y-2 mb-4">
        <div className="flex items-center gap-2 text-sm text-slate-400">
          <Mail className="w-4 h-4" />
          <span className="truncate">{employee.email}</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-slate-400">
          <Phone className="w-4 h-4" />
          <span>{employee.phone_number}</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-slate-400">
          <MapPin className="w-4 h-4" />
          <span>{employee.country}</span>
        </div>
      </div>

      {/* Security Features */}
      <div className="flex items-center gap-4 mb-4 text-xs">
        <div className="flex items-center gap-1">
          <div
            className={`w-2 h-2 rounded-full ${
              employee.is_2fa_enabled ? "bg-green-400" : "bg-slate-600"
            }`}
          />
          <span className="text-slate-400">2FA</span>
        </div>
        <div className="flex items-center gap-1">
          <div
            className={`w-2 h-2 rounded-full ${
              employee.is_verified ? "bg-green-400" : "bg-slate-600"
            }`}
          />
          <span className="text-slate-400">محقق</span>
        </div>
        <div className="flex items-center gap-1">
          <Shield className="w-3 h-3 text-purple-400" />
          <span className="text-slate-400">
            {employee.permissions_count || 0} صلاحية
          </span>
        </div>
      </div>

      {/* Last Activity */}
      {employee.last_activity && (
        <div className="bg-slate-900/50 rounded-lg p-3 border border-slate-700/30 mb-3">
          <div className="flex items-center gap-2 mb-1">
            <Activity className="w-4 h-4 text-blue-400" />
            <span className="text-sm font-medium text-slate-300">
              آخر نشاط
            </span>
          </div>
          <p className="text-xs text-slate-400 mb-1">
            {employee.last_activity.description}
          </p>
          <div className="flex items-center gap-1 text-xs text-slate-500">
            <Clock className="w-3 h-3" />
            {formatDate(employee.last_activity.created_at)}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex gap-2 mt-4 mb-3">
        <button
          onClick={handlePermissionsClick}
          className="flex items-center justify-center gap-2 flex-1 px-3 py-2 bg-purple-600/20 text-purple-400 rounded-lg hover:bg-purple-600/30 transition-colors text-sm font-medium border border-purple-600/30 hover:border-purple-500/50"
        >
          <Settings className="w-4 h-4" />
          الصلاحيات
        </button>
      </div>

      {/* Last Login */}
      <div className="pt-3 border-t border-slate-700/50">
        <div className="flex items-center justify-between text-xs text-slate-500">
          <span>آخر تسجيل دخول</span>
          <span>{formatDate(employee.last_login)}</span>
        </div>
      </div>
    </motion.div>
  );
};

export default EmployeeCard;